<script lang="ts">
    import { Button, Spinner, Input, Tooltip, Checkbox } from '@allibee/components/basic';
    import { SortableTree, type SortableTreeNodeData } from '@allibee/components/common';
    import { OverlayScrollbars } from 'overlayscrollbars';
    import { createEventDispatcher, onMount } from 'svelte';
    import { MtmService } from '$lib/api';
    import { twMerge } from 'tailwind-merge';
    import { isEmpty, isEqual, uniqBy } from 'lodash-es';
    import { highlightMatch } from '$lib/utils';
    import { _ } from 'svelte-i18n';
    import { page } from '$app/stores';
    import { getDivisionFullPath, type DivisionNode } from '@allibee/utils';

    type GroupTreeNode = SortableTreeNodeData & {
        data: MtmGroupDto;
        nodes?: GroupTreeNode[];
    };

    const dispatch = createEventDispatcher<{ confirm: AlexGroupDto[]; cancel: void }>();
    let treeRef: HTMLDivElement, selectedRef: HTMLDivElement;

    export let onConfirm = (v?: any) => v;
    export let confirmText = $_('lib.service.modalContent.modalDivisionSelect.confirm');
    export let onCancel: (v?: any) => void = () => false;
    export let multiple = false;
    export let color: 'primary' | 'hanwhas' = 'primary';
    export let defaultItems: AlexGroupDto[] | undefined;
    export let includeSubDivision = false;

    // TODO @maj0rika 한화도 primary를 쓴다면 제거
    const colorClass = color === 'primary' ? 'text-primary-500' : 'text-orange-500';
    const bgColorClass = color === 'primary' ? 'bg-primary-50' : 'bg-orange-50';
    const hoverBgColorClass = color === 'primary' ? 'hover:bg-primary-100' : 'hover:bg-orange-100';
    const borderColorClass = color === 'primary' ? 'border-primary-500' : 'border-orange-500';

    let title = $_('lib.service.modalContent.modalDivisionSelect.selectDivision');
    let loading = true;
    let divisions: DivisionNode[] = [];
    let searchKeyword = '';
    let searchedKeyword = '';
    let searchedDivisions: MtmIdAndNameDto[] = [];
    let selectedDivisions: MtmIdAndNameDto[] = !multiple && defaultItems?.[0] ? [defaultItems[0] as MtmIdAndNameDto] : [];
    let confirmedDivisions: AlexGroupDto[] = defaultItems || [];
    let selectedDivisionId = $page.data.auth.workspaceUserInfo.groupId;

    onMount(async () => {
        const response = await MtmService.getDivisionTree();
        divisions = response.data.data as DivisionNode[];
        loading = false;

        const refs = [treeRef, selectedRef];
        refs.forEach(ref => {
            if (ref) {
                OverlayScrollbars(ref, {
                    scrollbars: {
                        autoHide: 'scroll',
                    },
                });
            }
        });
        setTimeout(() => {
            updateRenderLabel();
        }, 100);
    });

    function handleConfirm() {
        const result = multiple
            ? confirmedDivisions
            : selectedDivisions[0]
              ? ([{ ...selectedDivisions[0], type: includeSubDivision ? 'INCLUDE_SUB_DIVISIONS' : 'SELF_DIVISION' }] as AlexGroupDto[])
              : [];
        dispatch('confirm', result);
        onConfirm?.(result);
    }

    function handleCancel() {
        dispatch('cancel');
        onCancel?.();
    }

    const convertDivisionToData = tree => {
        return (
            tree?.map(node => ({
                data: node.division,
                nodes: node.nodes ? convertDivisionToData(node.nodes) : [],
            })) || []
        );
    };

    let renderLabel = ((data: MtmGroupDto) => `
        <div id="render-label-${data.id}" class="flex gap-1 items-center">
            ${data.name}
            <svg id="render-label-icon-${data.id}" class="hidden" width="14" height="10" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M13.3192 0.480806C13.5629 0.724592 13.6998 1.05519 13.6998 1.39991C13.6998 1.74462 13.5629 2.07522 13.3192 2.31901L6.11912 9.51905C5.87533 9.76276 5.54469 9.89971 5.19998 9.89971C4.85526 9.89971 4.52466 9.7628 4.28088 9.51909L0.675917 5.91413C0.439112 5.66895 0.308122 5.34053 0.311084 4.99967C0.314046 4.65881 0.450766 4.33276 0.691796 4.09173C0.932827 3.8507 1.25888 3.71398 1.59974 3.71101C1.94059 3.70805 2.26898 3.83909 2.51416 4.07589L2.51916 4.08072L5.19998 6.76162L11.4808 0.480763C11.7246 0.237051 12.0553 0.100098 12.4 0.100098C12.7447 0.100098 13.0754 0.237093 13.3192 0.480806Z" fill="currentColor"/>
            </svg>
        </div>
    `) as unknown as import('@allibee/components/common').SortableTreeRenderLabelFunction;

    let groupNodes: GroupTreeNode[] = [];
    $: if (!isEmpty(divisions)) groupNodes = convertDivisionToData(divisions);

    const handleSearch = async (keyword: string) => {
        searchedKeyword = keyword;
        searchedDivisions = [];
        if (keyword === '') return;

        const response = await MtmService.pageGroups({ name: keyword });
        searchedDivisions =
            response.data.data.content?.map(item => ({
                id: item.id,
                name: item.name,
            })) || [];
        selectedDivisions = selectedDivisions.filter(dv => searchedDivisions?.find(sd => sd.id === dv.id));

        updateRenderLabel();
    };

    const updateRenderLabel = () => {
        document.querySelectorAll<HTMLElement>('[id^="render-label-"]').forEach(el => {
            el.classList.remove(colorClass);
        });

        document.querySelectorAll<HTMLElement>('[id^="render-label-icon-"]').forEach(el => {
            el.classList.remove(colorClass, 'block');
            el.classList.add('hidden');
        });

        selectedDivisions.forEach(dv => {
            const selectedLabel = document.getElementById(`render-label-${dv.id}`);
            if (selectedLabel) {
                selectedLabel.classList.add(colorClass);
            }

            const selectedIcon = document.getElementById(`render-label-icon-${dv.id}`);
            if (selectedIcon) {
                selectedIcon.classList.remove('hidden');
                selectedIcon.classList.add(colorClass, 'block');
            }
        });
    };

    const handleNodeClick = async (event, node) => {
        const idAndName: MtmIdAndNameDto = { id: node.data.id, name: node.data.name };
        const index = selectedDivisions.findIndex(dv => dv.id === idAndName.id && dv.name === idAndName.name);

        if (multiple) {
            if (index === -1) {
                selectedDivisions = [...selectedDivisions, idAndName];
            } else {
                selectedDivisions = selectedDivisions.filter(id => !isEqual(id, idAndName));
            }
        } else {
            if (index === -1) {
                selectedDivisions = [idAndName];
            } else {
                selectedDivisions = [];
            }
        }

        updateRenderLabel();
    };

    const handleDissmissDivision = division => {
        confirmedDivisions = confirmedDivisions.filter(u => u.id !== division.id);
    };

    function handleConfirmDivisions(type: 'INCLUDE_SUB_DIVISIONS' | 'SELF_DIVISION') {
        for (const division of selectedDivisions) {
            const duplicated = confirmedDivisions.find(item => item.id === division.id);

            if (duplicated) {
                // 이미 선택된 부서를 추가하려고 할 떄
                if (duplicated.type !== type) {
                    // id는 같지만, type이 다른 경우 (INCLUDE_SUB_DIVISIONS, SELF_DIVISION 모두 선택된 경우), INCLUDE_SUB_DIVISIONS가 항상 남도록
                    confirmedDivisions = [...confirmedDivisions.filter(item => item.id !== division.id), { ...division, type: 'INCLUDE_SUB_DIVISIONS' }];
                }
            } else {
                // 새로운 부서를 선택했을 떄, 새로 추가
                confirmedDivisions = [...confirmedDivisions, { ...division, type }];
            }
        }

        selectedDivisions = [];
        updateRenderLabel();
    }

    let icons = {
        collapsed: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="24" height="24" rx="4" fill="#E1E3EB"/><path d="M12 7.5V12M12 12V16.5M12 12H16.5M12 12H7.5" stroke="#636570" stroke-width="1.2" stroke-linecap="square" stroke-linejoin="round"/></svg>`,
        open: `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="0.5" y="0.5" width="23" height="23" rx="3.5" fill="white"/><rect x="0.5" y="0.5" width="23" height="23" rx="3.5" stroke="#C7C9D1"/><path d="M16.5 12H7.5" stroke="#636570" stroke-width="1.2" stroke-linecap="square" stroke-linejoin="round"/></svg>`,
    };
</script>

<div class="flex h-full w-full flex-col text-center">
    <section class="flex items-center justify-between rounded-t-lg border-b p-6">
        <h2 class="whitespace-pre p-0 text-lg font-semibold text-gray-900 dark:text-white">
            {title || ''}
        </h2>

        <button on:click={handleCancel} type="button">
            <svg class="m-0" width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 5.5L19 19.5M5 19.5L19 5.5L5 19.5Z" stroke="#C7C9D1" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
        </button>
    </section>

    <div class="flex max-h-full flex-col gap-2 overflow-y-auto overscroll-contain bg-gray-50 px-6 py-10" role="document" on:wheel|stopPropagation|passive>
        <div class="flex h-[30rem] items-center gap-4">
            <div class="inline-flex h-full w-full flex-col gap-4 overflow-hidden rounded-lg border border-gray-200 bg-white p-6">
                <div class="inline-flex gap-4">
                    <Input
                        placeholder={$_('lib.service.modalContent.modalDivisionSelect.inputDivisionName')}
                        bind:value={searchKeyword}
                        on:keydown={e => e.key === 'Enter' && handleSearch(searchKeyword)}
                    />
                    <Button color="gray" on:click={() => handleSearch(searchKeyword)}>{$_('lib.service.modalContent.modalDivisionSelect.search')}</Button>
                </div>
                <div bind:this={treeRef} class="w-full">
                    <div class={twMerge('modal-division-tree inline-flex h-full w-full flex-col gap-4 rounded-lg', loading && 'flex items-center justify-center')}>
                        {#if loading}
                            <Spinner size="8" />
                        {:else if searchedDivisions && searchedDivisions.length > 0}
                            {#each searchedDivisions as division}
                                {@const finded = selectedDivisions.some(dv => dv.id === division.id)}
                                <button
                                    on:click={() => {
                                        if (finded) {
                                            selectedDivisions = selectedDivisions.filter(dv => dv.id !== division.id);
                                        } else {
                                            if (multiple) {
                                                selectedDivisions = [...selectedDivisions, division];
                                                updateRenderLabel();
                                            } else {
                                                selectedDivisions = [division];
                                            }
                                        }
                                    }}
                                    class={twMerge(
                                        `inline-flex w-full items-center justify-between rounded-lg border border-gray-200 bg-white px-[0.625rem] py-2 focus:ring-0`,
                                        finded && `${bgColorClass} ${borderColorClass}`,
                                    )}
                                >
                                    <div class="flex items-center gap-1 rounded px-1 py-[0.125rem] hover:bg-gray-200 hover:mix-blend-multiply">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                            <path
                                                d="M12.375 2.8125H9.5625C9.26424 2.81284 8.97829 2.93148 8.76738 3.14238C8.55648 3.35329 8.43784 3.63924 8.4375 3.9375V8.4375H5.625V6.75C5.62466 6.45174 5.50602 6.16579 5.29512 5.95488C5.08421 5.74398 4.79826 5.62534 4.5 5.625H2.25C1.95174 5.62534 1.66579 5.74398 1.45488 5.95488C1.24398 6.16579 1.12534 6.45174 1.125 6.75L1.125 11.25C1.12534 11.5483 1.24398 11.8342 1.45488 12.0451C1.66579 12.256 1.95174 12.3747 2.25 12.375H4.5C4.79826 12.3747 5.08421 12.256 5.29512 12.0451C5.50602 11.8342 5.62466 11.5483 5.625 11.25L5.625 9.5625H8.4375V14.0625C8.43784 14.3608 8.55648 14.6467 8.76738 14.8576C8.97829 15.0685 9.26424 15.1872 9.5625 15.1875H12.375V16.875H16.875V12.375H12.375V14.0625H9.5625V9.5625H12.375V11.25H16.875V6.75L12.375 6.75V8.4375H9.5625V3.9375H12.375V5.625L16.875 5.625V1.125L12.375 1.125L12.375 2.8125ZM2.25 11.25L2.25 6.75H4.5V11.25H2.25ZM15.75 13.5V15.75H13.5V13.5H15.75ZM15.75 7.875V10.125H13.5V7.875L15.75 7.875ZM15.75 2.25L15.75 4.5L13.5 4.5L13.5 2.25L15.75 2.25Z"
                                                fill="#A8AAB3"
                                            />
                                        </svg>
                                        <p class="line-clamp-1 text-xs font-semibold text-gray-900">
                                            {@html highlightMatch(division.name, searchedKeyword, colorClass)}
                                        </p>
                                    </div>
                                    <Tooltip strategy="fixed">{getDivisionFullPath(division.id, divisions, ' / ') || division.name}</Tooltip>
                                    <Checkbox checked={finded} class="mr-0" />
                                </button>
                            {/each}
                        {/if}
                        <div class={twMerge('w-full', (loading || searchedDivisions?.length) && 'hidden')}>
                            <SortableTree selectedNodeId={selectedDivisionId} disableSorting nodes={groupNodes} initCollapseLevel={1} {renderLabel} {icons} onClick={handleNodeClick} />
                        </div>
                    </div>
                </div>
            </div>
            {#if multiple}
                <div class="flex flex-col gap-[0.625rem]">
                    {#if includeSubDivision}
                        <Button class="whitespace-nowrap" color="primary" on:click={() => handleConfirmDivisions('SELF_DIVISION')}>
                            {$_('lib.service.modalContent.modalDivisionSelect.addSelfDivision')}
                            <svg width="19" height="18" viewBox="0 0 19 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M10.25 3.75L15.5 9L10.25 14.25M4.25 3.75L9.5 9L4.25 14.25" stroke="white" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        </Button>
                        <Button class="whitespace-nowrap" color="dark" on:click={() => handleConfirmDivisions('INCLUDE_SUB_DIVISIONS')}>
                            {$_('lib.service.modalContent.modalDivisionSelect.addIncludingSubDivisions')}
                            <svg width="19" height="18" viewBox="0 0 19 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M10.25 3.75L15.5 9L10.25 14.25M4.25 3.75L9.5 9L4.25 14.25" stroke="white" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        </Button>
                    {:else}
                        <Button
                            class="whitespace-nowrap"
                            color="dark"
                            on:click={() => {
                                handleConfirmDivisions('SELF_DIVISION');
                            }}
                        >
                            {$_('lib.service.modalContent.modalDivisionSelect.add')}
                            <svg width="19" height="18" viewBox="0 0 19 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M10.25 3.75L15.5 9L10.25 14.25M4.25 3.75L9.5 9L4.25 14.25" stroke="white" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        </Button>
                    {/if}
                    <Button
                        color="dark"
                        outline
                        on:click={() => {
                            confirmedDivisions = [];
                        }}
                    >
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8.25 14.25L3 9L8.25 3.75M14.25 14.25L9 9L14.25 3.75" stroke="#636570" stroke-width="1.2" stroke-linecap="square" stroke-linejoin="round" />
                        </svg>
                        {$_('lib.service.modalContent.modalDivisionSelect.resetSelection')}
                    </Button>
                </div>

                <div class="inline-flex h-full w-60 shrink-0 flex-col rounded-lg border border-gray-200 bg-white text-left">
                    <div bind:this={selectedRef} class="w-full">
                        <div class="inline-flex w-full justify-between p-6">
                            <div class="shring-0 flex h-6 w-full justify-between">
                                <div class="text-base font-bold leading-normal text-black">{$_('lib.service.modalContent.modalDivisionSelect.selectedDivision')}</div>
                                <div class="flex">
                                    <div class={twMerge('text-sm font-bold', colorClass)}>{confirmedDivisions.length}</div>
                                    <div class="text-sm text-gray-900">{$_('lib.service.modalContent.modalDivisionSelect.count')}</div>
                                </div>
                            </div>
                        </div>
                        <div class="shring-0 inline-flex w-full flex-col gap-4 px-6">
                            {#if confirmedDivisions.length > 0}
                                {#each confirmedDivisions as division}
                                    <div class="inline-flex w-full items-center gap-1 p-1">
                                        <div class="flex flex-row items-center gap-1">
                                            <span class="flex-shrink-0">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="19" viewBox="0 0 18 19" fill="none">
                                                    <path
                                                        d="M12.375 3.3125H9.5625C9.26424 3.31284 8.97829 3.43148 8.76738 3.64238C8.55648 3.85329 8.43784 4.13924 8.4375 4.4375V8.9375H5.625V7.25C5.62466 6.95174 5.50602 6.66579 5.29512 6.45488C5.08421 6.24398 4.79826 6.12534 4.5 6.125H2.25C1.95174 6.12534 1.66579 6.24398 1.45488 6.45488C1.24398 6.66579 1.12534 6.95174 1.125 7.25L1.125 11.75C1.12534 12.0483 1.24398 12.3342 1.45488 12.5451C1.66579 12.756 1.95174 12.8747 2.25 12.875H4.5C4.79826 12.8747 5.08421 12.756 5.29512 12.5451C5.50602 12.3342 5.62466 12.0483 5.625 11.75L5.625 10.0625H8.4375V14.5625C8.43784 14.8608 8.55648 15.1467 8.76738 15.3576C8.97829 15.5685 9.26424 15.6872 9.5625 15.6875H12.375V17.375H16.875V12.875H12.375V14.5625H9.5625V10.0625H12.375V11.75H16.875V7.25L12.375 7.25V8.9375H9.5625V4.4375H12.375V6.125L16.875 6.125V1.625L12.375 1.625L12.375 3.3125ZM2.25 11.75L2.25 7.25H4.5V11.75H2.25ZM15.75 14V16.25H13.5V14H15.75ZM15.75 8.375V10.625H13.5V8.375L15.75 8.375ZM15.75 2.75L15.75 5L13.5 5L13.5 2.75L15.75 2.75Z"
                                                        fill="#A8AAB3"
                                                    />
                                                </svg>
                                            </span>
                                            <div class="text-sm font-medium text-gray-900">
                                                <span class="line-clamp-1">{division.name}</span>
                                                {#if division.type === 'INCLUDE_SUB_DIVISIONS'}
                                                    <span>{$_('lib.service.modalContent.modalDivisionSelect.includeSubDivisions')}</span>
                                                {/if}
                                            </div>
                                        </div>
                                        <button on:click={() => handleDissmissDivision(division)} class="flex-shrink-0">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="13" viewBox="0 0 12 13" fill="none">
                                                <path d="M3 3.5L9 9.5M3 9.5L9 3.5L3 9.5Z" stroke="#C7C9D1" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
                                            </svg>
                                        </button>
                                    </div>
                                    <Tooltip strategy="fixed">{getDivisionFullPath(division.id, divisions, ' / ') || division.name}</Tooltip>
                                {/each}
                            {:else}
                                <div class="shring-0 text-sm text-gray-400">{$_('lib.service.modalContent.modalDivisionSelect.noSelectedDivision')}</div>
                            {/if}
                        </div>
                    </div>
                </div>
            {/if}
        </div>
    </div>
    <section class="flex items-center justify-end gap-2 rounded-b-lg border-t p-6">
        <Button color="dark" class="flex w-fit" on:click={handleConfirm}>
            <slot name="confirm">
                {@html confirmText}
            </slot>
        </Button>
    </section>
</div>

<style global lang="postcss">
    .modal-division-tree .tree {
        --st-label-height: 2.5rem;
        --st-subnodes-padding-left: 2.7rem;
        --st-collapse-icon-width: 1.5rem;
        --st-collapse-icon-height: 3.75rem;
        --st-collapse-icon-size: 0.9rem;

        @apply my-2 flex flex-col;
        sortable-tree-node > :nth-child(3) {
            top: -0.35rem;
            left: calc(var(--st-collapse-icon-width) * -1 + -0.3125rem);
        }
        &__label {
            @apply cursor-pointer;
        }

        &__subnodes {
            @apply overflow-hidden;
        }

        --line-color: #c7c9d1;
        &__node {
            @apply relative;

            &:before {
                @apply absolute;
                content: '';
                top: 0;
                height: 101%;
                left: calc((var(--st-subnodes-padding-left) / -2) + 0.25rem);
                border-left: 0.0625rem solid var(--line-color);
            }
            &:last-child:before {
                top: -0.25rem;
                height: calc(100% - 1.1875rem);
            }
        }

        &:nth-child(1) > sortable-tree-node:nth-child(1):before {
            top: 1.5rem;
            height: calc(100% - 1.5rem);
        }

        &__label {
            @apply text-sm text-gray-900;
            position: relative;

            &:before {
                @apply absolute;
                content: '';
                left: -1.15rem;
                top: -1.5625rem;
                height: calc(100% + 0.375rem);
                width: 0.0625rem;
                border-left: 0.0625rem solid var(--line-color);
                mix-blend-mode: lighten;
            }
            &:first-child:before {
                content: none;
            }

            :last-child > &:before {
                height: calc(50% + 0.375rem);
            }

            &:after {
                content: '';
                position: absolute;
                left: -1.1rem;
                top: 0.0625rem;
                width: calc(1.4rem - 0.25rem);
                height: 50%;
                border-bottom: 0.0625rem solid var(--line-color);
                z-index: 1;
                mix-blend-mode: lighten;
            }
        }

        &__collapse {
            z-index: 3;
        }
    }
</style>
